import { Component, inject, signal, computed, OnInit, PLATFORM_ID } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { DialogModule } from 'primeng/dialog';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { InputTextModule } from 'primeng/inputtext';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { ToggleSwitchModule } from 'primeng/toggleswitch';

import { PaymentService, PaymentGateway } from '../../../services/payment';
import { ToastService } from '../../../services/toast';
import { PageTitleService } from '../../../services/page-title';

@Component({
  selector: 'app-payment-gateways',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    DialogModule,
    TableModule,
    ButtonModule,
    TagModule,
    TooltipModule,
    InputTextModule,
    IconFieldModule,
    InputIconModule,
    ToggleSwitchModule,
  ],
  templateUrl: './payment-gateways.html',
  styleUrls: ['./payment-gateways.css'],
})
export class PaymentGatewaysPage implements OnInit {
  private fb = inject(FormBuilder);
  private svc = inject(PaymentService);
  private toast = inject(ToastService);
  readonly pageTitle = inject(PageTitleService).title;
  private platformId = inject(PLATFORM_ID);

  loading = signal(false);
  saving = signal(false);
  editing = signal(false);
  showModal = signal(false);
  search = signal('');
  rows = signal<PaymentGateway[]>([]);

  filtered = computed(() => {
    const q = this.search().trim().toLowerCase();
    if (!q) return this.rows();
    return this.rows().filter(
      (r) =>
        (r.gatewayName || '').toLowerCase().includes(q) ||
        (r.gatewayCode || '').toLowerCase().includes(q) ||
        (r.baseUrl || '').toLowerCase().includes(q),
    );
  });

  form: FormGroup = this.fb.group({
    id: [0],
    gatewayCode: ['', Validators.required],
    gatewayName: ['', Validators.required],
    baseUrl: [null],
    logoUrl: [null],
    isActive: [true],
  });

  ngOnInit() {
    if (!isPlatformBrowser(this.platformId)) return;
    this.load();
  }

  load() {
    this.loading.set(true);
    this.svc.gwGetAll().subscribe({
      next: (res: any) => {
        this.loading.set(false);
        const data = res?.data ?? res ?? [];
        this.rows.set(Array.isArray(data) ? data : []);
      },
      error: () => this.loading.set(false),
    });
  }

  openAdd() {
    this.editing.set(false);
    this.form.reset({ id: 0, gatewayCode: '', gatewayName: '', baseUrl: null, logoUrl: null, isActive: true });
    this.showModal.set(true);
  }

  openEdit(r: PaymentGateway) {
    this.editing.set(true);
    this.form.reset({
      id: r.id,
      gatewayCode: r.gatewayCode ?? '',
      gatewayName: r.gatewayName ?? '',
      baseUrl: r.baseUrl ?? null,
      logoUrl: r.logoUrl ?? null,
      isActive: r.isActive ?? true,
    });
    this.showModal.set(true);
  }

  closeModal() {
    this.showModal.set(false);
  }

  /** Force the gateway code to uppercase as the user types. */
  onCodeInput() {
    const ctrl = this.form.get('gatewayCode');
    const up = (ctrl?.value ?? '').toString().toUpperCase();
    if (up !== ctrl?.value) ctrl?.setValue(up, { emitEvent: false });
  }

  save() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      if (this.form.get('gatewayCode')?.invalid) this.toast.warning('Gateway code is required.');
      else if (this.form.get('gatewayName')?.invalid) this.toast.warning('Gateway name is required.');
      else this.toast.warning('Please complete the required fields.');
      return;
    }
    const v = this.form.getRawValue();
    const dto: Partial<PaymentGateway> = {
      id: this.editing() ? Number(v.id) : 0,
      gatewayCode: (v.gatewayCode || '').trim().toUpperCase(),
      gatewayName: (v.gatewayName || '').trim(),
      baseUrl: this.blankToNull(v.baseUrl),
      logoUrl: this.blankToNull(v.logoUrl),
      isActive: !!v.isActive,
    };

    this.saving.set(true);
    this.svc.gwSave(dto).subscribe({
      next: (res: any) => {
        this.saving.set(false);
        if (res?.success === false) {
          this.toast.error(res?.message || 'Failed to save gateway.');
          return;
        }
        this.toast.success(res?.message || (this.editing() ? 'Gateway updated' : 'Gateway created'));
        this.closeModal();
        this.load();
      },
      error: () => this.saving.set(false),
    });
  }

  toggleActive(r: PaymentGateway) {
    const next = !r.isActive;
    this.patchRow(r.id, { isActive: next });
    this.svc.gwSetActive(r.id, next).subscribe({
      next: (res: any) => {
        if (res?.success === false) {
          this.patchRow(r.id, { isActive: r.isActive });
          this.toast.error(res?.message || 'Could not update status.');
        }
      },
      error: () => {
        this.patchRow(r.id, { isActive: r.isActive });
        this.toast.error('Could not update status.');
      },
    });
  }

  private patchRow(id: number, patch: Partial<PaymentGateway>) {
    this.rows.update((list) => list.map((x) => (x.id === id ? { ...x, ...patch } : x)));
  }

  async remove(r: PaymentGateway) {
    const ok = await this.toast.confirm(
      `${r.gatewayName} (${r.gatewayCode}) will be deactivated. You can re-add it later.`,
      'Delete gateway?',
      'Delete',
    );
    if (!ok) return;
    this.svc.gwDelete(r.id).subscribe({
      next: (res: any) => {
        if (res?.success === false) {
          this.toast.error(res?.message || 'Failed to delete.');
          return;
        }
        this.toast.success(res?.message || 'Gateway deleted');
        this.load();
      },
    });
  }

  private blankToNull(v: any): string | null {
    const s = (v ?? '').toString().trim();
    return s ? s : null;
  }
}
